import React, { useState, useRef, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { FaChevronDown } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import type { Option } from "../../../types/types";
import "./InputDropdown.scss";
import InputCheckbox from "../../common/Inputs/InputCheckbox/InputCheckbox";
import { useTranslation } from "react-i18next";

// Componente auxiliar para los checkboxes (puedes reemplazarlo por tu implementación)
// const InputCheckbox = ({
//   id,
//   label,
//   checked,
//   onChange,
// }: {
//   id: string;
//   label: string;
//   checked: boolean;
//   onChange: () => void;
// }) => (
//   <div style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
//     <input
//       type="checkbox"
//       id={id}
//       checked={checked}
//       onChange={onChange} // Permitimos que el checkbox gestione su propio click
//       readOnly
//       style={{ marginRight: "10px", pointerEvents: "none" }}
//     />
//     <label htmlFor={id} style={{ flex: 1 }}>
//       {label}
//     </label>
//   </div>
// );

const ChevronDownIcon = FaChevronDown as React.FC;
const CloseIcon = IoClose as React.FC;

interface EnhancedSelectProps {
    options: Option[];
    defaultValue?:
        | Option
        | Option[]
        | (string | number)[]
        | string
        | number
        | null;
    onChange?: (
        selected:
            | Option
            | Option[]
            | (string | number)[]
            | string
            | number
            | null
    ) => void;
    multiple?: boolean;
    searchable?: boolean;
    disabled?: boolean;
    tableMode?: boolean;
    placeholder?: string;
    label?: string;
    className?: string;
    onOpenChange?: (isOpen: boolean) => void;
    required?: boolean;
    returnFormat?: "object" | "value";
}

export function InputDropdown_New({
    multiple = false,
    searchable = true,
    disabled = false,
    tableMode = false,
    required = false,
    returnFormat = "object",
    placeholder = "Seleccionar...",
    label,
    className = "",
    options,
    defaultValue = null,
    onChange,
    onOpenChange,
}: EnhancedSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
    const [selectedValue, setSelectedValue] = useState<
        Option | Option[] | null
    >(null);
    const { t } = useTranslation();

    const containerRef = useRef<HTMLDivElement>(null);
    const controlRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (options.length === 0) return;

        const findOptionByValue = (val: any): Option | null => {
            if (val === null || val === undefined) return null;
            const valueToFind =
                typeof val === "object" && val.value !== undefined
                    ? val.value
                    : val;
            return (
                options.find(
                    (opt) => String(opt.value) === String(valueToFind)
                ) || null
            );
        };

        if (multiple) {
            if (Array.isArray(defaultValue) && defaultValue.length > 0) {
                const validValues = defaultValue
                    .map((dv) => findOptionByValue(dv))
                    .filter((opt): opt is Option => opt !== null);
                setSelectedValue(validValues.length > 0 ? validValues : null);
            } else {
                setSelectedValue(null);
            }
        } else {
            if (
                defaultValue !== null &&
                defaultValue !== undefined &&
                defaultValue !== ""
            ) {
                const foundOption = findOptionByValue(defaultValue);
                setSelectedValue(foundOption);
            } else {
                setSelectedValue(null);
            }
        }
    }, [defaultValue, options, multiple]);

    useEffect(() => {
        onOpenChange?.(isOpen);
        if (!isOpen) setSearch("");
    }, [isOpen, onOpenChange]);

    const filteredOptions = useMemo(
        () =>
            options.filter((opt) =>
                opt.label.toLowerCase().includes(search.toLowerCase())
            ),
        [options, search]
    );

    const toggleDropdown = () => {
        if (disabled) return;
        const newIsOpen = !isOpen;
        if (newIsOpen && tableMode && controlRef.current) {
            const rect = controlRef.current.getBoundingClientRect();
            setPosition({
                top: rect.bottom + window.scrollY + 4,
                left: rect.left + window.scrollX,
                width: rect.width,
            });
        }
        setIsOpen(newIsOpen);
    };

    const updateValue = (newValue: Option | Option[] | null) => {
        setSelectedValue(newValue);

        if (onChange) {
            if (returnFormat === "value") {
                if (Array.isArray(newValue)) {
                    onChange(newValue.map((opt) => opt.value));
                } else {
                    onChange(newValue ? newValue.value : null);
                }
            } else {
                onChange(newValue);
            }
        }
    };

    const handleOptionClick = (option: Option) => {
        if (multiple) {
            const currentValue = Array.isArray(selectedValue)
                ? selectedValue
                : [];
            const isSelected = currentValue.some(
                (o) => o.value === option.value
            );

            const newValue = isSelected
                ? currentValue.filter((o) => o.value !== option.value)
                : [...currentValue, option];

            updateValue(newValue.length > 0 ? newValue : null);
        } else {
            const isSameValue =
                selectedValue &&
                !Array.isArray(selectedValue) &&
                selectedValue.value === option.value;
            updateValue(isSameValue ? null : option);
            setIsOpen(false);
        }
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        updateValue(null);
    };

    const renderSelectedValue = () => {
        if (
            multiple &&
            Array.isArray(selectedValue) &&
            selectedValue.length > 0
        ) {
            return (
                <div className="input-dropdown__summary">
                    <span className="input-dropdown__summary-text">
                        {selectedValue.length}{" "}
                        {selectedValue.length === 1
                            ? "elemento seleccionado"
                            : "elementos seleccionados"}
                    </span>
                </div>
            );
        }

        if (!multiple && selectedValue && !Array.isArray(selectedValue)) {
            return (
                <span className="input-dropdown__value">
                    {selectedValue.label}
                </span>
            );
        }

        return (
            <span className="input-dropdown__placeholder">{placeholder}</span>
        );
    };

    const isSelected = (opt: Option) => {
        if (multiple && Array.isArray(selectedValue)) {
            return selectedValue.some((o) => o.value === opt.value);
        }
        if (!multiple && selectedValue && !Array.isArray(selectedValue)) {
            return selectedValue.value === opt.value;
        }
        return false;
    };

    const renderDropdownMenu = () => {
        if (!isOpen) return null;

        const menuContent = (
            <div
                className="input-dropdown__menu"
                style={
                    tableMode
                        ? {
                              position: "fixed",
                              top: `${position.top}px`,
                              left: `${position.left}px`,
                              width: `${position.width}px`,
                          }
                        : {}
                }
            >
                {searchable && (
                    <input
                        type="text"
                        className="input-dropdown__search"
                        placeholder="Buscar..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                    />
                )}
                <ul className="input-dropdown__options-list">
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((opt) => (
                            <li
                                key={opt.value}
                                className={`input-dropdown__option ${
                                    isSelected(opt) ? "selected" : ""
                                }`}
                                onClick={() => handleOptionClick(opt)}
                            >
                                {multiple ? (
                                    <InputCheckbox
                                        id={String(opt.value)}
                                        label={opt.label}
                                        checked={isSelected(opt)}
                                        onChange={() => handleOptionClick(opt)}
                                    />
                                ) : (
                                    opt.label
                                )}
                            </li>
                        ))
                    ) : (
                        <li className="input-dropdown__option--no-results">
                            No hay opciones
                        </li>
                    )}
                </ul>
            </div>
        );

        return tableMode
            ? ReactDOM.createPortal(menuContent, document.body)
            : menuContent;
    };

    const mainClasses = [
        "input-dropdown",
        className,
        disabled ? "input-dropdown--disabled" : "",
        tableMode ? "input-dropdown--table-mode" : "",
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div ref={containerRef} className={mainClasses}>
            {label && (
                <label className="input-dropdown__label">
                    {label}
                    {required && (
                        <span className="input-dropdown__required">*</span>
                    )}
                </label>
            )}
            <div
                ref={controlRef}
                className={`input-dropdown__control ${
                    isOpen ? "input-dropdown__control--open" : ""
                }`}
                onClick={toggleDropdown}
            >
                <div className="input-dropdown__value-container">
                    {renderSelectedValue()}
                </div>

                <div className="input-dropdown__indicators">
                    {!multiple && selectedValue && (
                        <span
                            className="input-dropdown__clear"
                            onClick={handleClear}
                        >
                            <CloseIcon />
                        </span>
                    )}
                    <span className="input-dropdown__arrow">
                        <ChevronDownIcon />
                    </span>
                </div>
            </div>
            {renderDropdownMenu()}
        </div>
    );
}

export default InputDropdown_New;
